import Dexie, { type Table } from 'dexie'
import type { Codec } from './crypto'
import type { DraftRow, OutboxOp, PendingBlob, VaultRecord, WorkingSetRow } from './types'

/**
 * The device-local store (doc §3): outbox + captured blobs + form drafts +
 * the cached working set, all partitioned by userId (shared tablets, §9).
 * One database per origin; per-user isolation is row-level via the userId
 * index — a signed-out user's queued work stays put, tied to their id,
 * never merged into or exposed to another user (§9).
 */
export class OfflineDb extends Dexie {
  outbox!: Table<OutboxOp, string>
  blobs!: Table<PendingBlob, string>
  drafts!: Table<DraftRow, string>
  workingSet!: Table<WorkingSetRow, string>
  /** Free-form persisted blobs (e.g. the TanStack Query cache snapshot). */
  kv!: Table<{ key: string; value: string }, string>
  /** Device credential vault (§9): enrolled users' wrapped data keys + salts
   * for PIN unlock / quick-switch. Holds NO secret and NO PIN — safe in the
   * clear; the wrapped key is only usable with the PIN. */
  vault!: Table<VaultRecord, string>

  /** When set (after a PIN unlock), sensitive at-rest values are AES-GCM
   * encrypted on write and decrypted on read. Undefined = plaintext (the
   * user has not enabled offline encryption on this device). */
  codec: Codec | undefined

  constructor(name = 'floorfile-offline') {
    super(name)
    this.version(1).stores({
      outbox: 'opId, userId, seq, status, entityId, [userId+status], [userId+entityId]',
      blobs: 'id, userId',
      drafts: 'key, userId',
      workingSet: 'userId',
      kv: 'key',
    })
    // v2 (additive): the device credential vault. Existing stores/data are
    // untouched by a version bump that only adds a table.
    this.version(2).stores({ vault: 'userId' })
  }
}

/** AsyncStorage-shaped adapter over the kv table — feeds the TanStack Query
 * persister so reads resolve from cache offline (doc §3). */
export function kvStorage(db: OfflineDb): {
  getItem: (key: string) => Promise<string | null>
  setItem: (key: string, value: string) => Promise<void>
  removeItem: (key: string) => Promise<void>
} {
  return {
    getItem: async (key) => {
      const stored = (await db.kv.get(key))?.value
      if (stored === undefined) return null
      // Values written while encryption was on carry an `enc:` marker. A codec
      // is required to read them back; a marker with no codec (locked) yields
      // a cache miss rather than throwing.
      if (stored.startsWith('enc:')) {
        if (!db.codec) return null
        try {
          return await db.codec.decrypt(stored.slice(4))
        } catch {
          return null
        }
      }
      return stored
    },
    setItem: async (key, value) => {
      if (!db.codec) {
        // FF-AUDIT-088: while the vault is LOCKED the query persister still
        // fires (it persists every cache change, and a locked boot's cache is
        // a handful of failed fetches). Writing that over an `enc:` entry
        // destroyed the encrypted snapshot seconds before the PIN unlock that
        // would have read it — and downgraded the slot to plaintext. An
        // encrypted entry is never overwritten without the codec that made it.
        const existing = (await db.kv.get(key))?.value
        if (existing?.startsWith('enc:')) return
      }
      const toStore = db.codec ? `enc:${await db.codec.encrypt(value)}` : value
      await db.kv.put({ key, value: toStore })
    },
    removeItem: async (key) => {
      await db.kv.delete(key)
    },
  }
}

/** Store a captured blob, encrypting its bytes at rest when a codec is active
 * (§9). Plaintext bytes otherwise — unchanged legacy behavior. */
/**
 * Device storage ran out. A distinct error type so callers can tell "the
 * tablet is full" (actionable by the worker: sync or discard) apart from a
 * generic IndexedDB fault (audit RUN-16). `navigator.storage.persist()` makes
 * our data non-evictable, so the browser will NOT silently reclaim space for
 * us — a full origin quota stays full until something is drained.
 */
export class OfflineStorageFullError extends Error {
  constructor() {
    super('This device is out of storage. Sync or discard queued work to free space.')
    this.name = 'OfflineStorageFullError'
  }
}

function isQuotaError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22)
}

export async function storeBlob(db: OfflineDb, id: string, userId: string, blob: Blob): Promise<void> {
  try {
    if (db.codec) {
      const enc = await db.codec.encryptBytes(await blob.arrayBuffer())
      await db.blobs.add({ id, userId, enc, mimeType: blob.type || 'application/octet-stream' })
    } else {
      await db.blobs.add({ id, userId, blob })
    }
  } catch (err) {
    // Previously this rejected with a raw DOMException that nothing caught, so
    // a full device surfaced to the technician as a silently lost capture.
    if (isQuotaError(err)) throw new OfflineStorageFullError()
    throw err
  }
}

/**
 * Drop terminal outbox rows (and their blobs) older than `maxAgeMs` (audit
 * RUN-16). `failed`/`conflict` ops are kept so the worker can retry or discard
 * them, but kept FOREVER they are just ballast — and on a shared tablet they
 * are the main way the origin quota fills up.
 */
export async function pruneTerminalOps(
  db: OfflineDb,
  userId: string,
  maxAgeMs: number,
  now = Date.now(),
): Promise<number> {
  const ops = await db.outbox.where('userId').equals(userId).toArray()
  let pruned = 0
  for (const op of ops) {
    if (op.status !== 'failed' && op.status !== 'conflict') continue
    if (now - new Date(op.createdAt).getTime() < maxAgeMs) continue
    await db.outbox.delete(op.opId)
    if (op.kind === 'attachment-upload') await db.blobs.delete(op.entityId)
    pruned += 1
  }
  return pruned
}

/** Read a captured blob back as a Blob, decrypting when it was stored
 * encrypted. Returns undefined if the row is gone (or unreadable while
 * locked). */
export async function readBlob(db: OfflineDb, id: string): Promise<Blob | undefined> {
  const row = await db.blobs.get(id)
  if (!row) return undefined
  if (row.enc) {
    if (!db.codec) return undefined
    const bytes = await db.codec.decryptBytes(row.enc)
    return new Blob([bytes], { type: row.mimeType ?? 'application/octet-stream' })
  }
  return row.blob
}

/** Explicit logout / device decommission clears the user's partition (§9);
 * pending outbox work is preserved unless `dropOutbox` is set. */
export async function clearUserPartition(
  db: OfflineDb,
  userId: string,
  options?: { dropOutbox?: boolean },
): Promise<void> {
  await db.drafts.where('userId').equals(userId).delete()
  await db.workingSet.where('userId').equals(userId).delete()
  if (options?.dropOutbox) {
    await db.outbox.where('userId').equals(userId).delete()
    await db.blobs.where('userId').equals(userId).delete()
  }
}
